import { API_BASE_URL } from '@/api/config';
import { ApiError } from '@/auth/api';
import type { NoteEntry } from '@/features/operations/types';
import type {
  CustomerDetail,
  CustomerEmailNotice,
  CustomerFormValues,
  CustomerListFilters,
  CustomerOperations,
  CustomerPage,
  CustomerTimelineEntry,
} from './types';

const CUSTOMERS_URL = `${API_BASE_URL}/api/customers`;

/** Keeps `details` (the `fieldErrors` of a form) so the page can mark each field. */
async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    const { error, ...details } = data;
    throw new ApiError(
      response.status,
      typeof error === 'string' && error ? error : 'Request failed',
      details,
    );
  }

  return data as T;
}

function headers(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export async function getCustomers(
  token: string,
  filters: CustomerListFilters & { email?: string },
): Promise<CustomerPage> {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.email) params.set('email', filters.email);
  if (filters.quoted) params.set('quoted', 'true');
  if (filters.sold) params.set('sold', 'true');
  if (filters.bought) params.set('bought', 'true');
  if (filters.page && filters.page > 1) params.set('page', String(filters.page));

  const query = params.toString();
  const response = await fetch(`${CUSTOMERS_URL}${query ? `?${query}` : ''}`, {
    headers: headers(token),
  });

  return parseJsonResponse<CustomerPage>(response);
}

export async function getCustomer(token: string, id: string): Promise<CustomerDetail> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}`, {
    headers: headers(token),
  });

  return (await parseJsonResponse<{ customer: CustomerDetail }>(response)).customer;
}

/// `created` is false when the mail already had an account: that account comes back instead.
export async function createCustomer(
  token: string,
  values: { fullName: string; email: string; phone?: string },
): Promise<{ customer: CustomerDetail; created: boolean }> {
  const response = await fetch(CUSTOMERS_URL, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(values),
  });

  return parseJsonResponse(response);
}

/// An empty `phone` clears it; the backend only allows that on a pending account.
export async function updateCustomer(
  token: string,
  id: string,
  values: Partial<CustomerFormValues>,
): Promise<{ customer: CustomerDetail; emailNotice: CustomerEmailNotice }> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: headers(token),
    body: JSON.stringify(values),
  });

  return parseJsonResponse(response);
}

export async function resendCustomerInvitation(token: string, id: string): Promise<CustomerDetail> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}/invitation`, {
    method: 'POST',
    headers: headers(token),
  });

  return (await parseJsonResponse<{ customer: CustomerDetail }>(response)).customer;
}

export async function getCustomerTimeline(
  token: string,
  id: string,
): Promise<CustomerTimelineEntry[]> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}/timeline`, {
    headers: headers(token),
  });

  return (await parseJsonResponse<{ entries: CustomerTimelineEntry[] }>(response)).entries;
}

export async function getCustomerOperations(
  token: string,
  id: string,
): Promise<CustomerOperations> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}/operations`, {
    headers: headers(token),
  });

  return parseJsonResponse<CustomerOperations>(response);
}

export async function addCustomerNote(token: string, id: string, body: string): Promise<NoteEntry> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ body }),
  });

  return (await parseJsonResponse<{ note: NoteEntry }>(response)).note;
}

export async function registerCustomerContact(token: string, id: string): Promise<NoteEntry> {
  const response = await fetch(`${CUSTOMERS_URL}/${encodeURIComponent(id)}/contacts`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ channel: 'WHATSAPP' }),
  });

  return (await parseJsonResponse<{ note: NoteEntry }>(response)).note;
}

/** The account of a mail, for the counter: null when that mail has none yet. */
export async function findCustomerByEmail(
  token: string,
  email: string,
): Promise<CustomerDetail | null> {
  const { customers } = await getCustomers(token, { email: email.trim().toLowerCase() });

  return customers[0] ? getCustomer(token, customers[0].id) : null;
}
